import { useEffect, useRef, useState, type ReactNode } from "react"
import styled from "styled-components"
import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import { formatCurrencyWithNumberToSymbolWithNumber as money } from "@ui/utils/formatter"
import { GD } from "src/GD"
import { transactionDetailsErrorMessage, type ITransactionDetails } from "src/services/transactionDetails"
import { ISSUER_FEE_NOTE, getFeeLabel, getFeePayerLabel } from "src/utils/transactionPresentation"
import { DS, DetailRow, Section } from "../ds/Kit"
import { useIsPrivateAccount } from "../private/PrivateAccountContext"

export const useTransactionDetails = (operationId: number | null | undefined) => {
	const [attempt, setAttempt] = useState(0)
	const [result, setResult] = useState<{ key: string; response: IAPIResponse<ITransactionDetails> }>()
	const request = useRef<{ key: string; promise: Promise<IAPIResponse<ITransactionDetails>> }>()
	const key = `${operationId}:${attempt}`
	useEffect(() => {
		let active = true
		if (!request.current || request.current.key !== key) {
			const promise = !Number.isSafeInteger(operationId) || Number(operationId) <= 0
				? Promise.resolve<IAPIResponse<ITransactionDetails>>({ error: { code: 1, message: "Invalid operation number" } })
				: Promise.resolve().then(() => GD.REQ_COMPANY_TRANSACTION_DETAILS.request({ operation_id: operationId! }))
					.catch(() => ({ error: { code: -1, message: "Could not load transaction details" } } as IAPIResponse<ITransactionDetails>))
			request.current = { key, promise }
		}
		void request.current.promise.then(response => { if (active) setResult({ key, response }) })
		return () => { active = false }
	}, [key, operationId])
	const response = result?.key === key ? result.response : undefined
	return { data: response?.data, error: response?.error, loading: !response, retry: () => setAttempt(value => value + 1) }
}

// ---- Shared presentation helpers (also used by TransactionDetails and InternalTransferDetails) ----

/** Dates in en-GB, e.g. "18 Aug 2026, 10:00". Empty string for missing or invalid input. */
export const formatDateTime = (value?: string | null) => {
	if (!value) return ""
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return ""
	return date.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

export type StatusTone = "good" | "warn" | "bad" | "neutral"

export const statusTone = (status?: string): StatusTone => {
	const value = (status || "").toLowerCase()
	if (["completed", "success", "approved", "paid", "active"].includes(value)) return "good"
	if (["pending", "processing", "waiting", "sent", "created"].includes(value)) return "warn"
	if (["error", "failed", "declined", "rejected", "blocked", "cancelled", "canceled"].includes(value)) return "bad"
	return "neutral"
}

export const statusLabel = (status?: string) => {
	const value = (status || "").replaceAll("_", " ").trim().toLowerCase()
	if (!value) return "Unknown"
	if (value === "error") return "Failed"
	return value[0].toUpperCase() + value.slice(1)
}

const TONES: Record<StatusTone, { bg: string; fg: string; line: string }> = {
	good: { bg: "#ECFDF5", fg: "#047857", line: "#A7F3D0" },
	warn: { bg: "#FFFBEB", fg: "#B45309", line: "#FDE68A" },
	bad: { bg: "#FEF2F2", fg: "#B91C1C", line: "#FECACA" },
	neutral: { bg: DS.soft, fg: DS.muted, line: DS.line }
}
export const toneColors = (tone: StatusTone) => TONES[tone]

export const StatusPill = ({ status }: { status?: string }) => {
	const tone = TONES[statusTone(status)]
	return (
		<HBox gap='6px' sx={{ alignItems: "center", width: "fit-content", padding: "3px 10px", borderRadius: "999px", border: `1px solid ${tone.line}`, background: tone.bg, color: tone.fg, fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>
			<Box aria-hidden sx={{ width: "6px", height: "6px", borderRadius: "50%", background: tone.fg }} />
			{statusLabel(status)}
		</HBox>
	)
}

const ICONS: Record<string, ReactNode> = {
	spent: <><rect x='2' y='5' width='20' height='14' rx='3' /><path d='M2 10h20' /><path d='M6 15h4' /></>,
	deposit: <><path d='M12 4v12' /><path d='m7 11 5 5 5-5' /><path d='M5 20h14' /></>,
	refund: <><path d='M9 14 4 9l5-5' /><path d='M4 9h10a6 6 0 0 1 0 12h-3' /></>,
	commission: <><path d='M19 5 5 19' /><circle cx='7' cy='7' r='2' /><circle cx='17' cy='17' r='2' /></>,
	transfer: <><path d='M7 7h13' /><path d='m16 3 4 4-4 4' /><path d='M17 17H4' /><path d='m8 13-4 4 4 4' /></>
}

/** Icon tile for the transaction type; tinted green for money in, brand colour for money out. */
export const TxIcon = ({ type, income, size = 48 }: { type?: string; income?: boolean; size?: number }) => {
	const key = type === "returned" ? "refund" : type && ICONS[type] ? type : "transfer"
	return (
		<Box aria-hidden sx={{ width: `${size}px`, height: `${size}px`, minWidth: `${size}px`, borderRadius: `${Math.round(size * 0.3)}px`, display: "flex", alignItems: "center", justifyContent: "center", background: income ? "#ECFDF5" : DS.accentSoft, color: income ? "#047857" : DS.accent }}>
			<svg width={size * 0.5} height={size * 0.5} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>{ICONS[key]}</svg>
		</Box>
	)
}

/** The amount the modal is about; emerald for money in, brand gradient for money out. */
export const AmountHero = ({ income, label, value, side }: { income?: boolean; label: string; value: ReactNode; side?: ReactNode }) => (
	<HBox
		sx={{
			position: "relative",
			overflow: "hidden",
			alignItems: "flex-end",
			justifyContent: "space-between",
			gap: "16px",
			flexWrap: "wrap",
			padding: "22px",
			borderRadius: DS.radius,
			color: "#FFFFFF",
			background: income ? "linear-gradient(135deg, #065F46 0%, #059669 100%)" : DS.heroGradient,
			boxShadow: income ? "0 12px 28px rgba(5, 150, 105, 0.26)" : "0 12px 28px rgba(30, 58, 95, 0.28)",
			"&::after": { content: '""', position: "absolute", right: "-40px", top: "-70px", width: "190px", height: "190px", borderRadius: "50%", background: "rgba(255,255,255,0.08)", pointerEvents: "none" }
		}}
	>
		<VBox gap='4px' sx={{ minWidth: 0 }}>
			<Typo sx={{ fontSize: "13px", fontWeight: 500, color: "rgba(255,255,255,0.8)" }}>{label}</Typo>
			<Typo sx={{ fontSize: "36px", fontWeight: 700, lineHeight: 1.1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere", color: "#FFFFFF" }}>{value}</Typo>
		</VBox>
		{side}
	</HBox>
)

/** From -> To cards. Pass plain text or interactive nodes. */
export const MoneyFlow = ({ from, to }: { from: ReactNode; to: ReactNode }) => (
	<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 32px minmax(0, 1fr)", gap: "10px", alignItems: "center", padding: "6px 0 14px", [DS.mobile]: { gridTemplateColumns: "minmax(0, 1fr)", gap: "6px" } }}>
		<VBox gap='6px' sx={{ minWidth: 0 }}>
			<Typo sx={{ fontSize: "12px", color: DS.muted }}>From</Typo>
			{from}
		</VBox>
		<Box aria-hidden sx={{ width: "32px", height: "32px", borderRadius: "50%", background: DS.soft, color: DS.accent, display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "end", justifySelf: "center", [DS.mobile]: { alignSelf: "center", transform: "rotate(90deg)" } }}>
			<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'><path d='M5 12h14' /><path d='m13 6 6 6-6 6' /></svg>
		</Box>
		<VBox gap='6px' sx={{ minWidth: 0 }}>
			<Typo sx={{ fontSize: "12px", color: DS.muted }}>To</Typo>
			{to}
		</VBox>
	</Box>
)

// ---- Live issuer / TRX details ----

const Disclosure = styled.details`
	min-width: 0;
	font-size: 13px;
	color: ${DS.ink};
	border-top: 1px solid ${DS.line};
	> summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 12px 0;
		min-height: 44px;
		box-sizing: border-box;
		cursor: pointer;
		font-weight: 600;
		line-height: 1.5;
		list-style: none;
		overflow-wrap: anywhere;
	}
	> summary::-webkit-details-marker { display: none; }
	> summary::after {
		content: "";
		flex-shrink: 0;
		width: 7px;
		height: 7px;
		border-right: 2px solid ${DS.faint};
		border-bottom: 2px solid ${DS.faint};
		transform: rotate(45deg);
		transition: transform 0.15s ease;
	}
	&[open] > summary::after { transform: rotate(-135deg); }
	> summary:hover { color: ${DS.accentInk}; }
	> summary:focus-visible { outline: 2px solid ${DS.accent}; outline-offset: 2px; border-radius: 6px; }
`
const JsonRow = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
	gap: 16px;
	padding: 8px 0;
	border-top: 1px solid ${DS.line};
	font-size: 12px;
	line-height: 1.5;
	color: ${DS.ink};
	> :first-child { color: ${DS.muted}; }
	> :last-child { text-align: right; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
	> * { min-width: 0; overflow-wrap: anywhere; }
	@media (max-width: 380px) {
		grid-template-columns: minmax(0, 1fr);
		gap: 4px;
		> :last-child { text-align: left; }
	}
`
const Notice = ({ children, alert = false }: { children: ReactNode; alert?: boolean }) => <Box role={alert ? "alert" : "status"} sx={{
	padding: "14px 16px", border: `1px solid ${alert ? "#FDE68A" : DS.line}`, borderRadius: "12px",
	backgroundColor: alert ? "#FFFBEB" : DS.soft, color: DS.ink, fontSize: "13px", lineHeight: 1.6,
}}>{children}</Box>
const Chip = ({ children }: { children: ReactNode }) => <Box sx={{ display: "inline-flex", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, color: DS.accentInk, background: DS.accentSoft, whiteSpace: "nowrap" }}>{children}</Box>

const scalarColor = (value: unknown) => typeof value === "string" ? "#047857" : value === null ? DS.faint : DS.accentInk
const ISSUER_KINDS: Record<string, string> = { card_transaction: "Card transaction", topup: "Top-up", topup_fee: "Top-up fee", withdrawal: "Withdrawal", event_fee: "Card transaction" }

// Preserve provider keys and every value, including nulls and array entries.
export const TransactionJson = ({ value }: { value: unknown }) => {
	if (value === null || typeof value !== "object") return <span style={{ overflowWrap: "anywhere", color: scalarColor(value) }}>{JSON.stringify(value) ?? "null"}</span>
	const entries = Object.entries(value)
	if (!entries.length) return <span>{Array.isArray(value) ? "[]" : "{}"}</span>
	return <div style={{ minWidth: 0 }}>{entries.map(([key, child]) => (
		child !== null && typeof child === "object" ? <Disclosure key={key}>
			<summary>{key}</summary>
			<div style={{ margin: "0 0 10px 6px", paddingLeft: "12px", borderLeft: `2px solid ${DS.line}` }}><TransactionJson value={child} /></div>
		</Disclosure> : <JsonRow key={key}><span>{key}</span><TransactionJson value={child} /></JsonRow>
	))}</div>
}

// Card issuer fields worth reading without opening the raw response; absent keys are skipped.
// ponytail: key names follow the card_transaction payload seen so far; other issuers fall back to the raw response.
const ISSUER_SUMMARY: [label: string, amount: string, currency?: string][] = [
	["Original amount", "amount", "currency"],
	["Authorized", "authorizedAmount", "authorizedCurrency"],
	["Settled", "settleAmount", "settleCurrency"],
	["Issuer fee", "fee", "feeCurrency"],
	["Cross-border fee", "crossBoardFee", "crossBoardFeeCurrency"],
]
const rawText = (raw: unknown, key: string) => {
	const value = raw && typeof raw === "object" ? (raw as Record<string, unknown>)[key] : undefined
	return typeof value === "string" || typeof value === "number" ? String(value).trim() : ""
}
export const issuerMerchantName = (raw: unknown) => rawText(raw, "merchantName")
export const issuerSummary = (raw: unknown): [string, string][] => {
	return ISSUER_SUMMARY.flatMap(([label, key, currency]) => {
		const value = rawText(raw, key)
		const unit = currency ? rawText(raw, currency) : ""
		return value ? [[label, unit ? `${value} ${unit}` : value] as [string, string]] : []
	})
}

/** Issuer amounts in the unified shape; null parts are skipped. Falls back to the raw payload when `amounts` is absent. */
export const issuerAmountRows = (details: ITransactionDetails["issuer"] | undefined): [string, string][] => {
	const amounts = details?.amounts
	if (!amounts) return issuerSummary(details?.raw)
	const parts: [string, keyof typeof amounts][] = [["Original amount", "original"], ["Authorized", "authorized"], ["Settled", "settled"], ["Issuer fee", "fee"], ["Cross-border fee", "cross_border_fee"]]
	return parts.flatMap(([label, key]) => {
		const part = amounts[key]
		return typeof part === "object" && part?.amount !== null && part?.amount !== undefined && part.currency ? [[label, money(part.amount, part.currency)] as [string, string]] : []
	})
}

const Chevron = () => <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: DS.faint }}><path d="m9 18 6-6-6-6" /></svg>

type FeeLine = NonNullable<ITransactionDetails["fees"]>[number]

/** A fee taken from this operation. With `onOpen` and an operation in the ledger the row opens that fee's details. */
const FeeLineRow = ({ line, onOpen }: { line: FeeLine; onOpen?: (operationId: number) => void }) => {
	const label = getFeeLabel(line.fee_code, line.label) + (line.status === "charged" ? "" : ` (${statusLabel(line.status === "failed" ? "error" : line.status)})`)
	// `operation_id: null` — the fee is not in the ledger yet, so there is nothing to open.
	const operationId = line.operation_id
	const clickable = Boolean(onOpen) && operationId !== null && operationId > 0
	return <Box
		element={clickable ? "button" : "div"}
		type={clickable ? "button" : undefined}
		aria-label={clickable ? `Open ${label}` : undefined}
		onClick={clickable ? () => onOpen!(operationId!) : undefined}
		sx={{
			display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", width: "100%", minHeight: "48px",
			padding: 0, border: 0, borderTop: `1px solid ${DS.line}`, background: "none", font: "inherit", textAlign: "left", color: DS.ink,
			...(clickable ? {
				cursor: "pointer",
				"&:hover > span:first-of-type": { color: DS.accentInk },
				"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "2px", borderRadius: "6px" },
			} : {}),
		}}
	>
		<Box element="span" sx={{ fontSize: "14px", color: DS.muted, minWidth: 0, overflowWrap: "anywhere" }}>{label}</Box>
		<HBox gap="6px" sx={{ alignItems: "center", flexShrink: 0 }}>
			{line.charged_by === "issuer" && <Chip>{ISSUER_FEE_NOTE}</Chip>}
			<Typo sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, fontVariantNumeric: "tabular-nums" }}>{money(line.amount, line.currency)}</Typo>
			{clickable && <Chevron />}
		</HBox>
	</Box>
}

const FeeLines = ({ fees, onOpen }: { fees: FeeLine[]; onOpen?: (operationId: number) => void }) => <section aria-label="Fees" style={{ minWidth: 0 }}>
	<Section title="Fees">
		{fees.map((line, index) => <FeeLineRow key={line.operation_id ?? `${line.fee_code}-${index}`} line={line} onOpen={onOpen} />)}
	</Section>
</section>

const FeeBlock = ({ fee, transaction, privateView, onOpen }: { fee: NonNullable<ITransactionDetails["fee"]>; transaction?: ICompanyTrxTransaction; privateView?: boolean; onOpen?: (operationId: number) => void }) => {
	const card = transaction?.card_name || fee.card_uid
	const buyer = transaction?.buyer_name || fee.buyer_uid
	const team = transaction?.team_name || fee.team_uid
	// A processing fee points at the company deposit it was taken from; other source refs are issuer trade numbers.
	const depositId = fee.source_kind === "company_deposit" ? Number(fee.source_ref) : NaN
	const showDeposit = Boolean(onOpen) && Number.isSafeInteger(depositId) && depositId > 0
	return <section aria-label="Fee" style={{ minWidth: 0 }}>
		<Section title={getFeeLabel(fee.fee_code, fee.label)} action={fee.charged_by === "issuer" ? <Chip>{ISSUER_FEE_NOTE}</Chip> : undefined}>
			<DetailRow label="Amount" value={money(fee.amount, fee.currency)} />
			{fee.payer && <DetailRow label="Payer" value={getFeePayerLabel(fee.payer, privateView)} />}
			{card && <DetailRow label="Card" value={card} />}
			{buyer && !privateView && <DetailRow label="Buyer" value={buyer} />}
			{team && !privateView && <DetailRow label="Team" value={team} />}
			{showDeposit && <DetailRow label="Taken from" value={
				<Box element="button" type="button" aria-label="Open the top-up this fee was taken from" onClick={() => onOpen!(depositId)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", border: 0, borderRadius: "10px", padding: "6px 10px", minHeight: "32px", fontFamily: "inherit", fontSize: "13px", fontWeight: 600, cursor: "pointer", color: DS.accentInk, background: DS.accentSoft, [DS.mobile]: { minHeight: "44px" } }}>
					Top-up<Chevron />
				</Box>
			} />}
		</Section>
	</section>
}

type Leg = ITransactionDetails["trx"]["legs"][number]
const PENDING_NOTE = "This transaction has not been settled by the issuer yet. Reopen it later to see the final status."
const FAILED_NOTE = "This transaction did not complete. The issuer details below include the reason when the issuer provides one."
const isExternal = (account: Leg["account"]) => !account || account.kind === "external" || account.kind === "merchant"

/** Human name for a ledger account; external accounts are shown as the merchant. */
export const legPartyName = (account: Leg["account"], merchantName?: string | null, privateView = false) => {
	if (isExternal(account)) return merchantName || "External account"
	if (account!.card) return [account!.card.name || "Card", account!.card.mask].filter(Boolean).join(" ")
	if (privateView) return "Your account"
	return account!.company?.name || account!.buyer?.name || account!.name || "Account"
}

/** Status plus where the funds went. Account numbers stay in the tooltip and the account popup. */
export const FundsFlow = ({ status, legs, fallback, merchantName, onAccount, privateView }: {
	status: string
	legs?: Leg[]
	fallback: ReactNode
	merchantName?: string | null
	onAccount?: (accountId: number) => void
	privateView?: boolean
}) => {
	const isPrivateView = useIsPrivateAccount() || Boolean(privateView)
	const tone = statusTone(status)
	return <section aria-label="Funds flow" style={{ minWidth: 0 }}>
		<Section title="Funds flow" action={<StatusPill status={status} />}>
			{(tone === "warn" || tone === "bad") && <Typo sx={{ margin: "4px 0 10px", padding: "12px 14px", borderRadius: "10px", fontSize: "13px", lineHeight: 1.5, background: TONES[tone].bg, color: TONES[tone].fg }}>{tone === "bad" ? FAILED_NOTE : PENDING_NOTE}</Typo>}
			{legs?.length ? legs.map(leg => {
				const income = leg.direction === "income"
				const name = legPartyName(leg.account, merchantName, isPrivateView)
				return <HBox key={leg.id} gap="12px" sx={{ alignItems: "center", padding: "12px 0", borderTop: `1px solid ${DS.line}`, minWidth: 0 }}>
					<Box aria-hidden="true" sx={{ display: "grid", placeItems: "center", width: "32px", height: "32px", borderRadius: "10px", backgroundColor: income ? "#ECFDF5" : DS.accentSoft, color: income ? "#047857" : DS.accent, flexShrink: 0 }}>
						<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>{income ? <><path d='M17 7 7 17' /><path d='M17 17H7V7' /></> : <><path d='M7 17 17 7' /><path d='M7 7h10v10' /></>}</svg>
					</Box>
					<VBox gap="2px" sx={{ flex: 1, minWidth: 0 }}>
						{onAccount
							? <Box element="button" type="button" title={`Account #${leg.account_id}`} aria-label={`${name}, account details`} onClick={() => onAccount(leg.account_id)} sx={{ border: 0, padding: 0, background: "none", font: "inherit", fontSize: "14px", fontWeight: 600, color: DS.ink, textAlign: "left", cursor: "pointer", overflowWrap: "anywhere", "&:hover": { color: DS.accentInk } }}>{name}</Box>
							: <Box title={`Account #${leg.account_id}`} sx={{ fontSize: "14px", fontWeight: 600, color: DS.ink, overflowWrap: "anywhere" }}>{name}</Box>}
						{!isExternal(leg.account) && <Typo sx={{ fontSize: "12px", color: DS.muted, fontVariantNumeric: "tabular-nums" }}>Balance after {money(leg.balance, leg.currency)}</Typo>}
					</VBox>
					<VBox gap="4px" sx={{ alignItems: "flex-end", flexShrink: 0 }}>
						<Typo sx={{ fontSize: "15px", fontWeight: 700, color: income ? "#047857" : DS.ink, fontVariantNumeric: "tabular-nums" }}>{income ? "+" : "−"}{money(Math.abs(leg.amount), leg.currency)}</Typo>
						{statusTone(leg.status) !== tone && <StatusPill status={leg.status} />}
					</VBox>
				</HBox>
			}) : fallback}
		</Section>
	</section>
}

/** Fee source, issuer record (debug data) and issuer notices. The caller renders the funds flow. */
/** `onOpenOperation` opens another operation's details (a fee row, the top-up behind a processing fee). */
export const TransactionDetailsLive = ({ data, error, loading, retry, transaction, privateView, onOpenOperation }: ReturnType<typeof useTransactionDetails> & { transaction?: ICompanyTrxTransaction; privateView?: boolean; onOpenOperation?: (operationId: number) => void }) => {
	const isPrivateView = useIsPrivateAccount() || Boolean(privateView)
	const issuer = data?.issuer
	const issuerError = data?.issuer_error
	const noDetails = issuerError?.code === 3021 || issuerError?.code === 3022
	// The fee block replaces the generic 3022 notice, except for top-up fees whose notice explains them.
	const hideNotice = Boolean(data?.fee) && issuerError?.code === 3022 && issuer?.kind !== "topup_fee"
	return <VBox gap="16px" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
		{loading && <Notice><HBox gap="10px" sx={{ alignItems: "center" }}><span aria-hidden="true" style={{ color: DS.accent }}>◌</span>Loading transaction details…</HBox></Notice>}
		{error && <Notice alert><VBox gap="10px">{transactionDetailsErrorMessage(error)} {error.code !== 3020 && error.code !== 1 && <Button title="Retry" onClick={retry} variant="secondarySmall" sx={{ alignSelf: "flex-start" }} />}</VBox></Notice>}
		{data?.fee && <FeeBlock fee={data.fee} transaction={transaction} privateView={isPrivateView} onOpen={onOpenOperation} />}
		{!!data?.fees?.length && <FeeLines fees={data.fees} onOpen={onOpenOperation} />}
		{issuer && <section aria-label="Issuer details" style={{ minWidth: 0 }}>
			<Section title={issuer.kind === "event_fee" ? "Issuer record of the original transaction" : "Issuer record"} action={<Chip>{ISSUER_KINDS[issuer.kind] ?? issuer.kind}</Chip>}>
				{issuer.issuer_reference && <DetailRow label="Reference" value={issuer.issuer_reference} mono copy={issuer.issuer_reference} />}
				{issuer.source && <DetailRow label="Source" value={issuer.source} />}
				{issuer.fetched_at && formatDateTime(issuer.fetched_at) && <DetailRow label="Fetched at" value={formatDateTime(issuer.fetched_at)} />}
				{issuer.matched_by === "heuristic" && <Box role="note" sx={{ margin: "6px 0 10px", padding: "10px 12px", borderRadius: "10px", backgroundColor: DS.accentSoft, color: DS.accentInk, fontSize: "12px", lineHeight: 1.5 }}>Matched by amount and time</Box>}
				{!issuerError && <Disclosure><summary>Live issuer response</summary><TransactionJson value={issuer.raw} /></Disclosure>}
				<Disclosure><summary>Local issuer archive</summary><TransactionJson value={issuer.local} /></Disclosure>
				<Disclosure><summary>Saved webhook</summary><TransactionJson value={issuer.webhook} /></Disclosure>
			</Section>
		</section>}
		{issuerError && !hideNotice && <Notice alert><VBox gap="8px">
			<Box sx={{ fontWeight: 600 }}>{issuerError.code === 3021 ? "The issuer does not provide operation details"
				: issuerError.code === 3022 ? "The card issuer has no extra details for this operation"
				: "Could not retrieve issuer data"}</Box>
			<div>{issuerError.details || issuerError.message}</div>
			{!noDetails && <Button title="Retry" onClick={retry} variant="secondarySmall" sx={{ alignSelf: "flex-start" }} />}
		</VBox></Notice>}
	</VBox>
}
